/**
 * Кастомная серия lightweight-charts: заливка между двумя рядами (upper/lower),
 * а не под одним рядом до низа панели — готового типа под это в библиотеке
 * нет (Area красит до низа панели, не между двумя кривыми). Рисует один
 * закрытый полигон: вперёд по upper, назад по lower — то же самое, что раньше
 * делал ribbon() в SVG-версии, просто через canvas-рендерер плагина.
 */
import {
  customSeriesDefaultOptions,
  type CustomData,
  type CustomSeriesOptions,
  type CustomSeriesPricePlotValues,
  type ICustomSeriesPaneRenderer,
  type ICustomSeriesPaneView,
  type PaneRendererCustomData,
  type PriceToCoordinateConverter,
  type Time,
  type WhitespaceData,
} from 'lightweight-charts'
import type { CanvasRenderingTarget2D } from 'fancy-canvas'

export interface BandData extends CustomData<Time> {
  upper: number
  lower: number
}

export interface BandSeriesOptions extends CustomSeriesOptions {
  fillColor: string
}

const defaultOptions: BandSeriesOptions = {
  ...customSeriesDefaultOptions,
  fillColor: 'rgba(0,0,0,0.1)',
}

class BandSeriesRenderer implements ICustomSeriesPaneRenderer {
  private data: PaneRendererCustomData<Time, BandData> | null = null
  private options: BandSeriesOptions | null = null

  update(data: PaneRendererCustomData<Time, BandData>, options: BandSeriesOptions): void {
    this.data = data
    this.options = options
  }

  draw(target: CanvasRenderingTarget2D, priceConverter: PriceToCoordinateConverter): void {
    target.useBitmapCoordinateSpace((scope) => {
      const data = this.data
      const options = this.options
      if (!data || !options || data.visibleRange === null) return
      const { bars, visibleRange } = data
      const ctx = scope.context
      const hr = scope.horizontalPixelRatio
      const vr = scope.verticalPixelRatio

      ctx.beginPath()
      let started = false
      for (let i = visibleRange.from; i < visibleRange.to; i++) {
        const bar = bars[i]
        const y = priceConverter(bar.originalData.upper)
        if (y == null) continue
        const x = bar.x * hr
        if (!started) {
          ctx.moveTo(x, y * vr)
          started = true
        } else {
          ctx.lineTo(x, y * vr)
        }
      }
      for (let i = visibleRange.to - 1; i >= visibleRange.from; i--) {
        const bar = bars[i]
        const y = priceConverter(bar.originalData.lower)
        if (y == null) continue
        ctx.lineTo(bar.x * hr, y * vr)
      }
      if (!started) return
      ctx.closePath()
      ctx.fillStyle = options.fillColor
      ctx.fill()
    })
  }
}

class BandSeriesView implements ICustomSeriesPaneView<Time, BandData, BandSeriesOptions> {
  private rendererInstance = new BandSeriesRenderer()

  priceValueBuilder(plotRow: BandData): CustomSeriesPricePlotValues {
    return [plotRow.lower, plotRow.upper, plotRow.upper]
  }

  isWhitespace(data: BandData | WhitespaceData<Time>): data is WhitespaceData<Time> {
    return (data as Partial<BandData>).upper === undefined
  }

  renderer(): ICustomSeriesPaneRenderer {
    return this.rendererInstance
  }

  update(data: PaneRendererCustomData<Time, BandData>, options: BandSeriesOptions): void {
    this.rendererInstance.update(data, options)
  }

  defaultOptions(): BandSeriesOptions {
    return defaultOptions
  }
}

export function createBandSeriesView(): ICustomSeriesPaneView<Time, BandData, BandSeriesOptions> {
  return new BandSeriesView()
}

/** Объединяет два ряда {time,value} в {time,upper,lower} по общим таймстемпам. */
export function toBand(a: { time: number; value: number }[], b: { time: number; value: number }[]): BandData[] {
  const byTime = new Map(b.map((p) => [p.time, p.value]))
  const out: BandData[] = []
  for (const p of a) {
    const other = byTime.get(p.time)
    if (other == null) continue
    out.push({
      time: p.time as Time,
      upper: Math.max(p.value, other),
      lower: Math.min(p.value, other),
    })
  }
  return out
}
